/**
 * Theme picker — opened from the themes button in the app header. Lists the
 * available themes with a small preview swatch; clicking one applies it
 * instantly (live, no confirm) and persists it. Uses design tokens so it
 * re-skins along with the rest of the app.
 */
import { createPortal } from 'react-dom';
import { THEME_OPTIONS, useTheme, type ThemeId } from '@/common/theme';

export function ThemeModal({ onClose }: { onClose: () => void }) {
  const [theme, setTheme] = useTheme();

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose a theme"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 360,
          background: 'var(--color-surface)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 14,
          boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
          padding: 18,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Theme</div>
        <div style={{ fontSize: 12.5, color: 'var(--color-text-faint)', marginBottom: 14 }}>
          Pick the look and feel for Xavior.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {THEME_OPTIONS.filter((opt) => !opt.hidden).map((opt) => {
            const selected = opt.id === theme;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTheme(opt.id as ThemeId)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 10,
                  background: selected ? 'var(--color-surface-hover)' : 'transparent',
                  border: `1.5px solid ${selected ? 'var(--color-brand)' : 'var(--color-border)'}`,
                  color: 'var(--color-text)',
                  font: 'inherit',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
              >
                {/* Preview swatch: desk/rail + panel — the theme's two real
                    background tones (no accent stripe). */}
                <span
                  aria-hidden="true"
                  style={{
                    display: 'flex', flexShrink: 0,
                    width: 44, height: 30, borderRadius: 6, overflow: 'hidden',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <span style={{ width: 18, background: opt.swatch[0] }} />
                  <span style={{ flex: 1, background: opt.swatch[1] }} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{opt.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-faint)' }}>{opt.hint}</span>
                </span>
                {selected && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
              background: 'var(--color-action-cancel-bg)', color: 'var(--color-action-cancel-text)',
              border: '1px solid var(--color-border)', font: 'inherit', fontSize: 13, fontWeight: 500,
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
